import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Globe, Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Language {
  code: string
  name: string
  flag: string
  nativeName: string
}

const languages: Language[] = [
  {
    code: 'pt',
    name: 'Portuguese',
    flag: '🇵🇹',
    nativeName: 'Português'
  },
  {
    code: 'fr',
    name: 'French',
    flag: '🇫🇷',
    nativeName: 'Français'
  },
  {
    code: 'en',
    name: 'English',
    flag: '🇺🇸',
    nativeName: 'English'
  },
  {
    code: 'es',
    name: 'Spanish',
    flag: '🇪🇸',
    nativeName: 'Español'
  }
]

interface SimpleLanguageSelectorProps {
  className?: string
}

export function SimpleLanguageSelector({ className }: SimpleLanguageSelectorProps) {
  const { i18n, t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState(
    languages.find(lang => lang.code === i18n.language) || languages[0]
  )
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handleLanguageChange = (language: Language) => {
    setSelectedLanguage(language)
    i18n.changeLanguage(language.code)
    localStorage.setItem('i18nextLng', language.code)
    setIsOpen(false)
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600",
          "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300",
          "hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors",
          "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-20"
        )}
      >
        <span className="text-lg">{selectedLanguage.flag}</span>
        <span className="font-medium text-sm">
          {selectedLanguage.nativeName}
        </span>
        <ChevronDown className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50">
          <div className="p-1">
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 text-left transition-colors rounded-md",
                  "hover:bg-gray-100 dark:hover:bg-gray-700",
                  selectedLanguage.code === language.code
                    ? "bg-primary text-white hover:bg-primary/90"
                    : "text-gray-700 dark:text-gray-300"
                )}
              >
                <span className="text-lg">{language.flag}</span>
                <div className="flex-1">
                  <div className="font-medium text-sm">
                    {language.nativeName}
                  </div>
                  <div className="text-xs opacity-75">{language.name}</div>
                </div>
                {selectedLanguage.code === language.code && (
                  <Check className="w-4 h-4" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}




